// usePostMilestones.js
import { act } from "react";
import { useEffect, useRef, useState } from "react";
import useUserScrolled from "./useUserScrolled";

const ACTION_THRESHOLDS = {
  A: 2,
  B: 5,
  C: 8,
  D: 11,
};

function computeActions(total) {
  if (total === 0) return [];
  if (total <= 3) return ["D"];
  if (total <= 6) return ["A", "D"];
  if (total <= 9) return ["A", "B", "D"];
  return ["A", "B", "C", "D"];
}

export default function usePostMilestones(posts, actionCallback) {
  // console.log("🔥 usePostMilestones called with posts:", posts);
  const total = posts.length;
  const [actions, setActions] = useState([]);
  const fired = useRef(new Set());
  const userScrolled = useUserScrolled();
  
  useEffect(() => {
    if (total === 0) return; // nothing loaded yet

    const sequence = computeActions(total);
    setActions(sequence);
  }, [total]);

  const triggerAction = (actionName) => {
    if (fired.current.has(actionName)) return;
    fired.current.add(actionName);
    actionCallback && actionCallback("flex", actionName);

    // console.log("🔥 Action Fired:", actionName);
  };

useEffect(() => {
  if (actions.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      if (!userScrolled) return;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const sentinel = entry.target;
        const postEl = sentinel.closest(".post-item");
        if (!postEl) return;

        const idx = Number(postEl.dataset.index);
        if (Number.isNaN(idx)) return;

        const next = actions[0];
        if (!next) return;
        // console.log(`Checking action ${next} at index ${idx}`, actions);

        let threshold = ACTION_THRESHOLDS[next];

        // For D → allow last post as alternative
        if (next === "D") {
          if (idx === total - 1 || idx >= threshold) {
            triggerAction("D");
            setActions((a) => a.slice(1));
          }
          return;
        }

        // A/B/C normal checks
        if (idx >= threshold) {
          triggerAction(next);
          setActions((a) => a.slice(1));
        }
      });
    },
    {
      threshold: 0,
      rootMargin: "0px 0px 0px 0px", // no hacks needed
    }
  );

  document
    .querySelectorAll(".post-bottom-sentinel")
    .forEach((el) => observer.observe(el));

  return () => observer.disconnect();
}, [actions, userScrolled]);

  return {};
}
